import { useState, useCallback } from "react";
import { FilterRepository } from "../repositories";

// Generic/placeholder organisation names to exclude from the filter list
const EXCLUDED_ORGANISATIONS = new Set([
  "An Auditor / Certification Scheme / Service Provider",
  "A Brand / Retailer",
  "A Multi-Stakeholder Initiative",
  "A Civil Society Organization",
]);

/**
 * Custom hook to manage filter state and operations
 * Handles drawer visibility, fetching filter options, selection state,
 * and applying/clearing filters with server-side facility filtering.
 */
export function useFilters(allMarkers) {
  // Drawer visibility
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  // Available filter options (fetched from edge functions)
  const [states, setStates] = useState([]);
  const [organisations, setOrganisations] = useState([]);

  // User's current selections (before applying)
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedOrganisations, setSelectedOrganisations] = useState([]);

  // Applied filters (after tapping "Apply")
  const [appliedStates, setAppliedStates] = useState([]);
  const [appliedOrganisations, setAppliedOrganisations] = useState([]);

  // Filtered marker IDs (from server)
  const [filteredMarkerIds, setFilteredMarkerIds] = useState(null);

  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState(null);
  const [noResults, setNoResults] = useState(false);

  // Whether lists have been fetched
  const [hasFetchedLists, setHasFetchedLists] = useState(false);

  // Derived: are any filters currently applied?
  const hasActiveFilters =
    appliedStates.length > 0 || appliedOrganisations.length > 0;

  // Derived: filtered markers array
  const filteredMarkers =
    filteredMarkerIds === null
      ? allMarkers
      : allMarkers.filter((m) => filteredMarkerIds.has(m.id));

  /**
   * Open filter drawer and fetch filter options if not already cached
   */
  const openDrawer = useCallback(async () => {
    setIsFilterDrawerOpen(true);
    setNoResults(false);

    if (!hasFetchedLists) {
      setIsLoading(true);
      setError(null);
      try {
        const [statesData, orgsData] = await Promise.all([
          FilterRepository.getFilterStates(),
          FilterRepository.getFilterOrganisations(),
        ]);
        setStates(statesData);
        setOrganisations(orgsData.filter((org) => !EXCLUDED_ORGANISATIONS.has(org)));
        setHasFetchedLists(true);
      } catch (err) {
        console.error("[useFilters] Failed to fetch filter options:", err);
        setError("Failed to load filter options. Please try again.");
      } finally {
        setIsLoading(false);
      }
    }
  }, [hasFetchedLists]);

  /**
   * Close filter drawer
   */
  const closeDrawer = useCallback(() => {
    setIsFilterDrawerOpen(false);
  }, []);

  /**
   * Toggle a state selection
   */
  const toggleState = useCallback((stateName) => {
    setSelectedStates((prev) =>
      prev.includes(stateName)
        ? prev.filter((s) => s !== stateName)
        : [...prev, stateName]
    );
  }, []);

  /**
   * Toggle an organisation selection
   */
  const toggleOrganisation = useCallback((orgName) => {
    setSelectedOrganisations((prev) =>
      prev.includes(orgName)
        ? prev.filter((o) => o !== orgName)
        : [...prev, orgName]
    );
  }, []);

  /**
   * Apply selected filters — calls the server-side filter edge function
   */
  const applyFilters = useCallback(async () => {
    // If nothing selected, clear filters
    if (selectedStates.length === 0 && selectedOrganisations.length === 0) {
      setAppliedStates([]);
      setAppliedOrganisations([]);
      setFilteredMarkerIds(null);
      setIsFilterDrawerOpen(false);
      setNoResults(false);
      return;
    }

    setIsApplying(true);
    try {
      const facilityIds = await FilterRepository.filterFacilities(
        selectedStates,
        selectedOrganisations
      );

      setAppliedStates([...selectedStates]);
      setAppliedOrganisations([...selectedOrganisations]);
      setFilteredMarkerIds(new Set(facilityIds));
      
      if (facilityIds.length === 0) {
        setNoResults(true);
      } else {
        setNoResults(false);
        setIsFilterDrawerOpen(false);
      }
    } catch (err) {
      console.error("[useFilters] Failed to apply filters:", err);
      setError("Failed to apply filters. Please try again.");
    } finally {
      setIsApplying(false);
    }
  }, [selectedStates, selectedOrganisations]);

  /**
   * Clear all selections and applied filters
   */
  const clearFilters = useCallback(() => {
    setSelectedStates([]);
    setSelectedOrganisations([]);
    setAppliedStates([]);
    setAppliedOrganisations([]);
    setFilteredMarkerIds(null);
    setNoResults(false);
  }, []);

  return {
    // Drawer state
    isFilterDrawerOpen,
    openDrawer,
    closeDrawer,

    // Filter options
    states,
    organisations,
    isLoading,
    error,

    // Selections
    selectedStates,
    selectedOrganisations,
    toggleState,
    toggleOrganisation,

    // Applied filters
    appliedStates,
    appliedOrganisations,
    hasActiveFilters,
    isApplying,
    noResults,

    // Actions
    applyFilters,
    clearFilters,

    // Filtered data
    filteredMarkers,
  };
}
